/* eslint-disable no-prototype-builtins */
import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { Link } from 'react-router-dom'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Avatar from '@mui/material/Avatar'

import useWindowDimensions from '../../helpers/windowDimensions.js'
import { validEmail, handleChange, Input, reloadView } from '../../helpers/globalHelpers.js'
import { positionChangeWidthSm, profPicDefaultURL, positionChangeWidthMd, positionChangeHeightSm, navbarHeight, footerHeight, dashboardNavbarHeight, bottomSteps } from '../../helpers/variableDefaults.js'
import { userIsAuthenticated, getPayload, getTokenFromLocalStorage } from '../../helpers/storage.js'
import { standardButton, replaceImageWithIconButton } from '../../helpers/buttons.js'
import { standardErrorContainer } from '../../helpers/errors.js'
import { standardSpinner } from '../../helpers/spinners.js'
import { loginTextField } from '../../helpers/textfields.js'
import { handleImageSelect } from '../../helpers/profilePictureElements.js'

import { userProfilieSectionTitle, titleAndEditButton, personalDetailsTextFieldOrTypography, personalDetailsTypography, userProfileEstateElement } from '../../helpers/settingsElements.js'

import { seoPageTags, customAnalyticsEvent } from '../../helpers/analytics.js'

// Settings Page
const Settings = () => {
  // Window Dimensions
  const { height, width } = useWindowDimensions()
  
  // Navigate
  const navigate = useNavigate()

  // Params
  const { userId } = useParams()

  // Payload
  const payload = getPayload()

  // User
  const [user, setUser] = useState({})
  const [previousUser, setPreviousUser] = useState({})

  // Edit Modes
  const [editPersonalDetails, setEditPersonalDetails] = useState(false)

  // Loading
  const [loading, setLoading] = useState(false)
  const [loadingPersonalDetailsEdits, setLoadingPersonalDetailsEdits] = useState(false)

  // Errors
  const [uploadPersonalEditsErrors, setUploadPersonalEditsErrors] = useState(false)
  const [paymentDetailsErrors, setPaymentDetailsErrors] = useState(false)
  const [showManagePayment, setShowManagePayments] = useState(false)

  const resetErrors = () => {
    setUploadPersonalEditsErrors(false)
    setPaymentDetailsErrors(false)
  }

  const resetLoading = () => {
    setLoadingPersonalDetailsEdits(false)
  }

  // UseEffect — Get User data and set estates state
  useEffect(() => {
    // console.log('my account use effect runs')

    if (!userIsAuthenticated() || !userId) {
      navigate(`/login`)
    } else if (userId !== payload.sub) {
      navigate(`/settings/${payload.sub}`)
      // reloadView()
    } else {
      const fetchData = async () => {
        setLoading(true)
        // console.log('userId ->', userId)
        try {
          const { data: retrievedUser } = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/users/${payload.sub}/not-populated`, {
            headers: {
              Authorization: `Bearer ${getTokenFromLocalStorage()}`,
            },
          })

          // console.log('retrievedUser ->', retrievedUser)

          if (retrievedUser._id === payload.sub) {
            
            // Set User State
            setUser(retrievedUser)
            setPreviousUser(retrievedUser)

            setLoading(false)

          } else {
            navigate('/')
            setLoading(false)
          }

        } catch (err) {
          // console.log(err)

          navigate('/')
          setLoading(false)
        }
      }
  
      fetchData()
    }
  }, [])

  // Upload modifications in real time to server
  const uploadModifiedUser = async (modifiedPropertiesObj) => {
    // console.log('uploadModifiedUser runs')
    
    try {

      // PUT request with updated profile object
      // await axios.put(`/api/users/${userId}`, newForm, {
      await axios.put(`${process.env.REACT_APP_SERVER_URL}/api/users/${userId}`, modifiedPropertiesObj, {
        headers: {
          Authorization: `Bearer ${getTokenFromLocalStorage()}`,
        },
      })
      
    } catch (error) {
      // console.log(error)

      // error message posting new profile
      resetLoading()

      setUploadPersonalEditsErrors(true)
      
    }

  }

  const resetPasswordPressed = () => {
    // console.log('resetPasswordPressed')
    resetErrors()
    navigate('/reset-password')
  }

  const editPersonalDetailsPressed = () => {
    // console.log('editPersonalDetailsPressed')

    setUser(previousUser)
    
    resetErrors()

    setEditPersonalDetails(!editPersonalDetails)
  }

  const savePersonalDetailsPressed = async () => {
    // console.log('savePersonalDetailsPressed')

    setLoadingPersonalDetailsEdits(true)

    // Modify User Object to Backend
    const modifiedPropertiesObj = { 
      email: user.email,
    }
    await uploadModifiedUser(modifiedPropertiesObj)

    setPreviousUser(user)

    setLoadingPersonalDetailsEdits(false)
    setEditPersonalDetails(false)
  }

  const handleManagePaymentPressed = async () => {
    // e.preventDefault()
    console.log('handleManagePaymentPressed runs')

    try {
      setLoading(true)

      const { data } = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/customers/${payload.sub}`, {
        headers: {
          Authorization: `Bearer ${getTokenFromLocalStorage()}`,
        },
      })
      // console.log('data ->', data)

      if (('message' in data) && data.message === 'Unauthorized') {
        setPaymentDetailsErrors(true)
      } else {
        window.location.href = data.url
      }

      // setLoading(false)

    } catch (error) {
      // console.log('error connecting to customer portal ->', error)

      setLoading(false)
    }
  }


  return (
    <>
      {/* Helmet — for analytics, seo, and page title changing */}
      {seoPageTags(
        'Settings'
      )}

      {/* Body */}

      {loading ?
        <Box sx={{ 
          width: '100%',
          // minHeight: `calc(100vh - ${navbarHeight} - ${footerHeight} - ${dashboardNavbarHeight})`,
          minHeight: `calc(100vh - ${navbarHeight} - ${dashboardNavbarHeight})`,
          mt: 0, mb: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
        }}>
          {standardSpinner()}
        </Box>
        : 
        <Box 
          sx={{ 
            width: '100%',
            minHeight: `calc(100vh - ${navbarHeight} - ${footerHeight})`,
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', 
          }}
        >
          <Box sx={{ mt: 4, mb: 4, width: '100%', maxWidth: '600px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>

            {/* This must be first input So that the file upload only fires when you press the button */}
            <Box sx={{ width: '100%' }}>
              <Input type="text" autoFocus="autoFocus" />
            </Box>

            {/* Personal Details (Email, Password) */}
            <Box sx={{ mt: width < positionChangeWidthSm ? 2 : 0, mb: 0, width: '90%', maxWidth: '450px' }}>
              
              {/* Personal Details — Title and Edit/Save/Cancel Buttons */}
              {/* {titleAndEditButton('Personal Details', editPersonalDetails, user, savePersonalDetailsPressed, editPersonalDetailsPressed, previousUser )} */}
              
              <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                {userProfilieSectionTitle('Personal Details')}
              </Box>

              {/* Error Message, if errors */}
              {uploadPersonalEditsErrors &&
                standardErrorContainer(
                  'Error uploading changes. Please try again.',
                  0,
                  0
                )
              }
              <Box 
                sx={{
                  backgroundColor: editPersonalDetails ? 'white' : 'whitesmoke',
                  mt: 1,
                  pl: 3, pr: 3, pt: 1, pb: 1,
                  borderRadius: '5px',
                  boxShadow: 3,
                  border: editPersonalDetails ? 0 : 2,
                  borderColor: 'black',
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                }}
              >
                {loadingPersonalDetailsEdits ?
                  standardSpinner()
                  :
                  <>
                    
                    {/* Email */}
                    {personalDetailsTextFieldOrTypography('email', 'Email', user, setUser, editPersonalDetails, setUploadPersonalEditsErrors)}
                    
                    {/* Password and Reset Password Button */}
                    {!editPersonalDetails &&
                      <Box 
                        sx={{
                          mt: editPersonalDetails ? 2 : 0,
                          width: '100%',
                          display: 'flex', flexDirection: 'row', justifyContent: 'center',
                        }}
                      >
                        {personalDetailsTypography('Password')}
                        
                        <Box textAlign={'right'} sx={{ width: '50%' }}>
                          {standardButton(
                            'Reset Password', 
                            'button',
                            'text',
                            !user.subscriptionIsActive,
                            'secondary', 
                            0,
                            0,
                            0,
                            '137px',
                            '30px',
                            resetPasswordPressed
                          )}
                        </Box>
                      </Box>
                    }
                    
                  </>
                }
                
              </Box>
            </Box>

            {/* Manage Payment */}
            {showManagePayment &&
              <Box sx={{ mt: width < positionChangeWidthSm ? 2 : 4, mb: 0, pb: 4, width: '90%', maxWidth: '450px' }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center' }}>
                  {userProfilieSectionTitle('Payment Method')}
                </Box>
                <Box 
                  sx={{
                    backgroundColor: 'whitesmoke',
                    mt: 1,
                    pl: 3, pr: 3, pt: 1, pb: 1,
                    borderRadius: '5px',
                    boxShadow: 3,
                    border: 2,
                    borderColor: 'black',
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {standardButton(
                      'Manage Payment', 
                      'button',
                      'contained',
                      false,
                      'primary', 
                      2,
                      2,
                      0,
                      '225px', 
                      '45px', 
                      handleManagePaymentPressed
                    )}
                  </Box>

                  {paymentDetailsErrors && standardErrorContainer(
                    `No Payment Info to Manage`,
                    2,
                    0
                  )}
                  
                </Box>
              </Box>
            }

          </Box>
        </Box>
      }
    </>
  )
}

export default Settings